import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function YearlyChartData({ data }) {
  const pdfRef = useRef();
  if (!data?.length) return <p>No data available</p>;

  // =========================
  // Dynamic Year Detection
  // =========================
  const years = Object.keys(data[0]).filter(
    (key) => key !== "sex_label" && key !== "calculated_average",
  );

  const processed = data.map((row) => ({
    label: row.sex_label,
    values: years.map((year) => parseFloat(row[year]) || 0),
  }));

  const latestYear = years[years.length - 1];

  const highestLatest = processed.reduce(
    (max, row) =>
      row.values[years.length - 1] > max.values[years.length - 1] ? row : max,
    processed[0],
  );

  const totalLatest = processed.reduce(
    (sum, row) => sum + row.values[years.length - 1],
    0,
  );

  // =========================
  // Brand Colors
  // =========================
  const palette = {
    "All Sex": "#014CB2",
    Female: "#ec4899",
    Male: "#0ea5e9",
  };

  const chartData = {
    labels: years,
    datasets: processed.map((row) => ({
      label: row.label,
      data: row.values,
      backgroundColor: palette[row.label] || "#6366f1",
      borderRadius: 6,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Yearly Employment Totals by Sex (Stacked)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (context) =>
            `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
        },
        ticks: {
          callback: (value) => value.toLocaleString(),
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);

      pdf.save("yearly_stacked_totals.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Yearly Employment Totals Dashboard
        </h2>

        {/* KPI Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest {latestYear}</p>
            <p className="text-xl font-bold text-blue-800">
              {highestLatest.label}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total {latestYear}</p>
            <p className="text-xl font-bold text-green-800">
              {totalLatest.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Years Covered</p>
            <p className="text-xl font-bold text-yellow-800">
              {years[0]} – {latestYear}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-center border-collapse">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Sex</th>
                  {years.map((year) => (
                    <th key={year} className="p-2">
                      {year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {processed.map((row) => (
                  <tr
                    key={row.label}
                    className={`${
                      row.label === highestLatest.label
                        ? "bg-blue-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 font-medium">{row.label}</td>
                    {row.values.map((val, idx) => (
                      <td key={idx} className="p-2">
                        {val.toLocaleString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="min-w-[400px] w-full h-96">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
